// src/components/ComputoSALTab.js
// Computo Metrico / SAL — vive dentro ogni commessa. Stessa filosofia di un foglio Excel:
// righe sempre pronte da compilare, colonne di avanzamento che si aggiungono da sole ogni
// volta che si emette un nuovo Stato Avanzamento Lavori, riepilogo finale automatico.
import React, { useState, useEffect, useMemo } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, getDoc, setDoc, getDocs, runTransaction } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Plus, Trash2, ChevronLeft, Calendar, FileSpreadsheet, Upload, X } from "lucide-react";
import { calcolaSAL, nuovaRigaSAL, nuovoPeriodoSAL, totaleRiga, totaleSalRigaPeriodo, percCumulataRiga, testoConGrassetto, previsioneCompletamento } from "../utils/calcoloSAL";
import XLSXStyle from "xlsx-js-style";
import * as XLSX from "xlsx";
import { toLocalDateStr } from "../utils/dateUtils";

const RIGHE_VUOTE_MINIME = 5;

function fmt(n) {
  return (parseFloat(n)||0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtPerc(n) {
  return (parseFloat(n)||0).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
function dataItaliana(iso) {
  if (!iso) return "";
  return iso.split("-").reverse().join("/");
}

const PALETTE_COLORI = ["", "#fef9c3", "#fed7aa", "#fecaca", "#bbf7d0", "#bfdbfe", "#e9d5ff", "#f5d0fe"];

function TavolozzaColori({ colore, onScegli, onChiudi }) {
  return (
    <div style={{position:"absolute",zIndex:20,background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,padding:6,boxShadow:"0 4px 12px rgba(0,0,0,.12)",display:"flex",gap:4}}
      onMouseLeave={onChiudi}>
      {PALETTE_COLORI.map(c=>(
        <button key={c||"nessuno"} type="button" onClick={()=>{onScegli(c); onChiudi();}}
          title={c?"":"Nessun colore"}
          style={{width:18,height:18,borderRadius:4,cursor:"pointer",
            background:c||"#fff",
            border:c===colore?"2px solid #7c3aed":"1px solid #d1d5db",
            backgroundImage: c ? "none" : "linear-gradient(45deg,#fff 45%,#fca5a5 50%,#fff 55%)"}}/>
      ))}
    </div>
  );
}

// Navigazione con le frecce come in Excel — su/giù passano sempre alla cella corrispondente
// nella riga sopra/sotto, sinistra/destra passano alla cella adiacente solo quando il
// cursore è già all'inizio o alla fine del testo (altrimenti si muove dentro il testo).
function onKeyDownNav(e, colonne) {
  const el = e.target;
  const riga = parseInt(el.dataset.r, 10);
  const col = parseInt(el.dataset.c, 10);
  const vai = (r,c) => {
    const target = document.querySelector(`[data-r="${r}"][data-c="${c}"]`);
    if (target) { e.preventDefault(); target.focus(); target.select?.(); }
  };
  if (e.key==="ArrowUp") { vai(riga-1, col); return; }
  if (e.key==="ArrowDown") { vai(riga+1, col); return; }
  if (e.key==="ArrowLeft") {
    const alConfine = el.selectionStart===0 && el.selectionEnd===0;
    if (alConfine || el.type==="number") vai(riga, col-1);
    return;
  }
  if (e.key==="ArrowRight") {
    const lung = el.value?.length||0;
    const alConfine = el.selectionStart===lung && el.selectionEnd===lung;
    if (alConfine || el.type==="number") vai(riga, col+1);
    return;
  }
}

export default function ComputoSALTab({ projectId, projectName, codiceCommessa, clienteNome, orgId, responsabileNome }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [computo, setComputo] = useState(null);
  const [listinoMateriali, setListinoMateriali] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [caricato, setCaricato] = useState(false);
  const [showNuovoPeriodo, setShowNuovoPeriodo] = useState(false);
  const [showImporta, setShowImporta] = useState(false);
  const [passoImporta, setPassoImporta] = useState(1); // 1=carica file, 2=mappa colonne, 3=anteprima
  const [righeGrezze, setRigheGrezze] = useState([]); // array di array, tutto il foglio letto
  const [mappaColonne, setMappaColonne] = useState({}); // {colIdx: "descrizione"|"um"|"quantita"|"prezzoUnitario"|"titolo"|""}
  const [importando, setImportando] = useState(false);
  const [dataNuovoPeriodo, setDataNuovoPeriodo] = useState(toLocalDateStr(new Date()));

  useEffect(() => {
    if (!projectId || !orgId) return;
    return onSnapshot(query(collection(db,"computiSAL"), where("projectId","==",projectId), where("orgId","==",orgId)),
      snap => {
        if (snap.empty) { setComputo(null); setCaricato(true); return; }
        const d = snap.docs[0];
        setComputo({ id:d.id, ...d.data() });
        setCaricato(true);
      });
  }, [projectId, orgId]);

  useEffect(() => {
    if (!orgId) return;
    getDoc(doc(db,"organizations",orgId)).then(snap=>{if(snap.exists())setOrgInfo(snap.data());});
  }, [orgId]);

  // Listino Materiali, per la ricerca rapida dentro le righe — stesso meccanismo già
  // collaudato in Elaborazione Offerte e Rientro Materiali.
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"listinoMateriali"), where("organizationId","==",orgId)),
      snap => setListinoMateriali(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId]);

  const righe = computo?.righe || [];
  const periodi = computo?.periodi || [];
  const fatturato = computo?.fatturato || 0;
  // Con richieste di fatturazione sulla commessa il fatturato arriva da quelle «Fatturate» e
  // le richieste in attesa si tolgono dal «da richiedere» (utils/fatturazioneCommessa.js)
  const fatturatoDaRichieste = !!computo?.fatturatoDaRichieste;
  const richiestoInAttesa = computo?.richiestoInAttesa || 0;

  const dati = useMemo(() => calcolaSAL(righe, periodi, fatturato, richiestoInAttesa), [righe, periodi, fatturato, richiestoInAttesa]);
  const previsione = useMemo(() => previsioneCompletamento(periodi, dati.cumulativi), [periodi, dati.cumulativi]);

  const creaComputo = async () => {
    const nuoveRighe = Array.from({length:RIGHE_VUOTE_MINIME}, ()=>nuovaRigaSAL());
    await addDoc(collection(db,"computiSAL"), {
      projectId, projectName, codiceCommessa: codiceCommessa||"", clienteNome: clienteNome||"", orgId,
      titolo: projectName||"", righe: nuoveRighe, periodi: [], fatturato: 0,
      compilatoreUid: currentUser?.uid, compilatoreNome: currentUser?.displayName||"",
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    addToast("Computo Metrico creato ✅","success");
  };

  const salvaRighe = async (nuoveRighe) => {
    let righeFinali = [...nuoveRighe];
    let vuoteInCoda = 0;
    for (let i=righeFinali.length-1; i>=0; i--) {
      const r = righeFinali[i];
      if (!r.descrizione?.trim() && !r.quantita && !r.prezzoUnitario) vuoteInCoda++;
      else break;
    }
    while (vuoteInCoda < RIGHE_VUOTE_MINIME) { righeFinali.push(nuovaRigaSAL()); vuoteInCoda++; }
    await updateDoc(doc(db,"computiSAL",computo.id), { righe: righeFinali, updatedAt: serverTimestamp() });
  };

  const aggiornaRiga = (id, campo, valore) => {
    const nuove = righe.map(r => r.id===id ? {...r,[campo]:valore} : r);
    salvaRighe(nuove);
  };
  const aggiornaSalRiga = (id, periodoId, campo, valore) => {
    const nuove = righe.map(r => r.id===id ? {...r, sal:{...r.sal, [periodoId]:{...r.sal?.[periodoId], [campo]:valore}}} : r);
    salvaRighe(nuove);
  };
  const eliminaRiga = async (id) => {
    if (!await confirm("Eliminare questa riga?",{type:"danger"})) return;
    salvaRighe(righe.filter(r=>r.id!==id));
  };

  const aggiungiPeriodo = async () => {
    // Transazione: legge l'array "periodi" fresco dal server invece di fidarsi dello stato
    // locale — se due persone aggiungono un periodo nello stesso istante, la seconda
    // transazione vede già il periodo della prima e non lo sovrascrive per errore.
    const computoRef = doc(db,"computiSAL",computo.id);
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(computoRef);
      const periodiAttuali = snap.exists() ? (snap.data().periodi || []) : [];
      const nuovo = { ...nuovoPeriodoSAL(dataNuovoPeriodo), numero: periodiAttuali.length+1 };
      transaction.update(computoRef, { periodi:[...periodiAttuali,nuovo], updatedAt: serverTimestamp() });
    });
    setShowNuovoPeriodo(false);
    addToast(`Nuovo SAL al ${dataItaliana(dataNuovoPeriodo)} aggiunto ✅`,"success");
  };

  // Numero progressivo del SAL — permanente, non cambia mai anche se il periodo viene
  // riaperto e richiuso. Per i periodi creati prima di questa funzione (senza "numero"
  // salvato), lo ricostruisco dalla posizione nell'array, così anche lo storico vecchio
  // mostra un numero coerente.
  const numeroSAL = (per) => per.numero || (periodi.findIndex(p=>p.id===per.id)+1);
  const idSAL = (per) => `SAL-${codiceCommessa||"?"}-${String(numeroSAL(per)).padStart(3,"0")}`;

  // Il periodo più recente resta "bozza" finché non lo salvi apposta — solo dopo puoi
  // crearne uno successivo, così un SAL già emesso non cambia più sotto ai piedi di nessuno.
  const periodoAperto = periodi.length>0 && periodi[periodi.length-1].stato!=="salvato" ? periodi[periodi.length-1] : null;
  // L'importazione ha senso solo in fase iniziale, prima che qualunque SAL sia stato
  // cristallizzato — una volta che esiste uno storico salvato, importare righe in blocco
  // rischia di confondere l'avanzamento già registrato.
  const importazioneBloccata = periodi.some(p => p.stato==="salvato");
  const CAMPI_MAPPABILI = [
    { key:"", label:"— Ignora —" },
    { key:"descrizione", label:"Descrizione" },
    { key:"um", label:"Unità di Misura" },
    { key:"quantita", label:"Quantità" },
    { key:"prezzoUnitario", label:"Prezzo Unitario" },
    { key:"titolo", label:"È un titolo/categoria? (SI/X = sì)" },
  ];

  const leggiFileImportazione = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type:"array" });
        const primoFoglio = wb.Sheets[wb.SheetNames[0]];
        const aoa = XLSX.utils.sheet_to_json(primoFoglio, { header:1, defval:"" });
        if (aoa.length < 2) { addToast("Il file sembra vuoto — deve avere almeno un'intestazione e una riga di dati","error"); return; }
        setRigheGrezze(aoa);
        // Provo a indovinare la mappatura dai nomi delle colonne, tanto per dare un punto di partenza
        const intestazioni = aoa[0].map(h=>String(h||"").toLowerCase());
        const indovina = {};
        intestazioni.forEach((h,i) => {
          if (/descriz/.test(h)) indovina[i]="descrizione";
          else if (/^u\.?m\.?$|unit.*misura/.test(h)) indovina[i]="um";
          else if (/q\.?t[àa]|quantit/.test(h)) indovina[i]="quantita";
          else if (/prezzo|imp\.?\s*unit/.test(h)) indovina[i]="prezzoUnitario";
        });
        setMappaColonne(indovina);
        setPassoImporta(2);
      } catch (err) {
        console.error("Errore lettura file importazione:", err);
        addToast("Impossibile leggere il file — assicurati che sia un vero file Excel (.xlsx)","error");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const confermaImportazione = async () => {
    const idxDescrizione = Object.entries(mappaColonne).find(([,v])=>v==="descrizione")?.[0];
    if (idxDescrizione===undefined) { addToast("Devi almeno indicare quale colonna è la Descrizione","error"); return; }
    setImportando(true);
    try {
      const idxUm = Object.entries(mappaColonne).find(([,v])=>v==="um")?.[0];
      const idxQta = Object.entries(mappaColonne).find(([,v])=>v==="quantita")?.[0];
      const idxPrezzo = Object.entries(mappaColonne).find(([,v])=>v==="prezzoUnitario")?.[0];
      const idxTitolo = Object.entries(mappaColonne).find(([,v])=>v==="titolo")?.[0];

      const nuoveRighe = righeGrezze.slice(1) // salto l'intestazione
        .filter(r => String(r[idxDescrizione]||"").trim())
        .map(r => {
          const base = nuovaRigaSAL();
          base.descrizione = String(r[idxDescrizione]||"").trim();
          if (idxUm!==undefined) base.um = String(r[idxUm]||"").trim();
          if (idxQta!==undefined) base.quantita = parseFloat(r[idxQta])||"";
          if (idxPrezzo!==undefined) base.prezzoUnitario = parseFloat(r[idxPrezzo])||"";
          if (idxTitolo!==undefined) base.titolo = /^(s[iì]|x|yes|true|1)$/i.test(String(r[idxTitolo]||"").trim());
          return base;
        });

      if (nuoveRighe.length===0) { addToast("Nessuna riga valida trovata da importare","error"); setImportando(false); return; }

      // Le righe importate si aggiungono a quelle esistenti, senza mai toccare o
      // sovrascrivere righe già presenti — anche se hanno già percentuali SAL segnate.
      await salvaRighe([...righe.filter(r=>r.descrizione?.trim()||totaleRiga(r)>0), ...nuoveRighe]);
      addToast(`${nuoveRighe.length} riga/e importate ✅`,"success");
      setShowImporta(false); setPassoImporta(1); setRigheGrezze([]); setMappaColonne({});
    } catch (err) {
      console.error("Errore importazione:", err);
      addToast("Errore durante l'importazione: "+(err?.message||"errore sconosciuto"),"error");
    } finally {
      setImportando(false);
    }
  };

  const esportaExcel = () => {
    const testataPeriodi = periodi.flatMap(per => [`% SAL ${dataItaliana(per.data)}`, `Totale SAL ${dataItaliana(per.data)}`, `Note SAL ${dataItaliana(per.data)}`]);
    const intestazione = ["Pos","Descrizione","U.M.","Q.tà","Imp. Unit.","Totale", ...testataPeriodi];

    const righeDati = righe.filter(r=>r.descrizione?.trim()||totaleRiga(r)>0).map(r => {
      const tot = totaleRiga(r);
      const pos = dati.numerazione[r.id];
      const descPiana = (r.descrizione||"").replace(/\*\*/g,""); // Excel non ha il markdown, tolgo solo gli asterischi
      const colonneSal = periodi.flatMap(per => {
        const perc = r.sal?.[per.id]?.perc || "";
        const totPer = tot>0 && perc ? totaleSalRigaPeriodo(r,per.id) : "";
        return [perc, totPer, r.sal?.[per.id]?.note||""];
      });
      return { riga: [pos||"", descPiana, r.um||"", r.quantita||"", r.prezzoUnitario||"", tot||"", ...colonneSal], titolo: r.titolo };
    });

    const aoa = [
      [computo.titolo||projectName||"Computo Metrico"], [`${codiceCommessa?`[${codiceCommessa}] `:""}${clienteNome||""}`], [],
      intestazione,
      ...righeDati.map(r=>r.riga),
      [],
      ["Importo complessivo commessa","","","","",dati.totaleGenerale],
      ["Avanzamento precedente","","","","",`${fmtPerc(dati.avanzamentoPrecedente)}%`],
      ["Avanzamento del periodo","","","","",`${fmtPerc(dati.avanzamentoDelPeriodo)}%`],
      ["Avanzamento complessivo","","","","",`${fmtPerc(dati.avanzamentoComplessivo)}%`],
      ["Importo SAL maturato (progressivo)","","","","",dati.salMaturato],
      ["Fatturato","","","","",dati.fatturato],
      ["Da fatturare","","","","",dati.daFatturare],
      ["Residuo contrattuale","","","","",dati.residuoContrattuale],
    ];

    const ws = XLSXStyle.utils.aoa_to_sheet(aoa);
    const rigaIntestazione = 3; // indice 0-based della riga "Pos/Descrizione/..."
    const nCol = intestazione.length;
    for (let c=0;c<nCol;c++) {
      const addr = XLSXStyle.utils.encode_cell({r:rigaIntestazione,c});
      if (ws[addr]) ws[addr].s = { font:{bold:true,color:{rgb:"FFFFFF"}}, fill:{fgColor:{rgb:"1E1B4B"}} };
    }
    righeDati.forEach((r,i) => {
      if (!r.titolo) return;
      const rIdx = rigaIntestazione+1+i;
      for (let c=0;c<nCol;c++) {
        const addr = XLSXStyle.utils.encode_cell({r:rIdx,c});
        if (ws[addr]) ws[addr].s = { font:{bold:true}, fill:{fgColor:{rgb:"F5F3FF"}} };
      }
    });
    const addrTitolo = XLSXStyle.utils.encode_cell({r:0,c:0});
    if (ws[addrTitolo]) ws[addrTitolo].s = { font:{bold:true,sz:14} };

    ws["!cols"] = [{wch:5},{wch:40},{wch:8},{wch:8},{wch:10},{wch:12}, ...periodi.flatMap(()=>[{wch:8},{wch:12},{wch:16}])];

    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, ws, "Computo Metrico");
    XLSXStyle.writeFile(wb, `Computo_Metrico_${(projectName||"commessa").replace(/[^a-z0-9]/gi,"_")}.xlsx`);
  };

  const stampaPDF = () => {
    const win = window.open("","_blank");
    const coloreDoc = orgInfo?.coloreDocumenti || "#1e1b4b";
    const posizioneLogo = orgInfo?.posizioneLogo || "sinistra";
    const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:42px;object-fit:contain;"/>` : `<div style="font-size:18px;font-weight:900;color:${coloreDoc};">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;

    // Questo PDF documenta lo stato al momento dell'ultimo SAL registrato — se non ce n'è
    // ancora nessuno, ID e data restano vuoti, il documento resta comunque stampabile.
    const ultimoPeriodoStampa = periodi.length>0 ? periodi[periodi.length-1] : null;

    const righeHtml = righe.filter(r=>r.descrizione?.trim()||totaleRiga(r)>0).map(r => {
      const tot = totaleRiga(r);
      const pos = dati.numerazione[r.id];
      const parti = testoConGrassetto(r.descrizione);
      const descHtml = typeof parti==="string" ? "" : parti.map(p=>p.bold?`<strong>${p.testo}</strong>`:p.testo).join("");
      const salCelle = periodi.map(per => {
        const perc = r.sal?.[per.id]?.perc;
        const totPer = tot>0 && perc ? totaleSalRigaPeriodo(r,per.id) : null;
        return `<td style="text-align:right;">${perc?perc+"%":""}</td><td style="text-align:right;">${totPer!==null?"€"+fmt(totPer):""}</td><td>${r.sal?.[per.id]?.note||""}</td>`;
      }).join("");
      // Non spezzare mai una lavorazione tra due pagine; un titolo di gruppo resta sempre
      // unito alla riga che lo segue (mai da solo in fondo pagina).
      return `<tr style="background:${r.colore||(r.titolo?"#f5f3ff":"transparent")};font-weight:${r.titolo?700:400};page-break-inside:avoid;${r.titolo?"page-break-after:avoid;":""}">
        <td style="text-align:center;color:#d97706;font-weight:700;">${pos||""}</td>
        <td>${descHtml}</td>
        <td>${r.um||""}</td>
        <td style="text-align:right;">${r.quantita||""}</td>
        <td style="text-align:right;">${r.prezzoUnitario?fmt(r.prezzoUnitario):""}</td>
        <td style="text-align:right;font-weight:700;color:${tot>0?"#059669":"#9ca3af"};">${tot>0?"€"+fmt(tot):""}</td>
        ${salCelle}
      </tr>`;
    }).join("");

    const intestazioniSal = periodi.map(per=>`<th colspan="3" style="text-align:center;border-left:2px solid rgba(255,255,255,.3);">SAL al ${dataItaliana(per.data)}</th>`).join("");
    const sottointestazioniSal = periodi.map(()=>`<th style="border-left:2px solid #e5e7eb;">%</th><th>Totale SAL</th><th>Note</th>`).join("");

    // Storico compatto — l'evoluzione della commessa, un colpo d'occhio su tutti i SAL
    // passati senza dover scorrere l'ampia tabella con le colonne di ogni periodo.
    const storicoHtml = periodi.map((per,i) => `<tr>
        <td style="font-family:monospace;font-weight:700;color:${coloreDoc};">${idSAL(per)}</td>
        <td>${dataItaliana(per.data)}</td>
        <td style="text-align:right;font-weight:700;">${fmtPerc(dati.cumulativi[i]||0)}%</td>
      </tr>`).join("");

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>${ultimoPeriodoStampa?idSAL(ultimoPeriodoStampa):"SAL"} — ${projectName||""}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:10.5px;color:#111;padding:12mm 10mm 18mm;}
      @media screen{body{max-width:1200px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 landscape;margin:12mm 10mm 18mm;
        @bottom-center{content:"Pagina " counter(page) " di " counter(pages);font-size:9px;color:#9ca3af;font-family:Arial,sans-serif;}}
      @media print{.no-print{display:none;}}
      table{width:100%;border-collapse:collapse;margin-top:10px;}
      th{background:${coloreDoc};color:#fff;padding:5px 6px;font-size:8.5px;text-align:left;text-transform:uppercase;}
      td{padding:4px 6px;border-bottom:1px solid #f3f4f6;font-size:10px;}
      thead{display:table-header-group;} /* le intestazioni si ripetono su ogni pagina */
    </style></head><body>
      <div style="text-align:center;margin-bottom:4px;">
        <div style="font-size:20px;font-weight:900;color:${coloreDoc};letter-spacing:.03em;">STATO AVANZAMENTO LAVORI (SAL)</div>
        ${ultimoPeriodoStampa ? `<div style="font-size:10.5px;color:#6b7280;margin-top:3px;">
          <span style="font-family:monospace;font-weight:700;color:#d97706;">${idSAL(ultimoPeriodoStampa)}</span>
          &nbsp;·&nbsp; Data: ${dataItaliana(ultimoPeriodoStampa.data)}
        </div>` : `<div style="font-size:10.5px;color:#9ca3af;margin-top:3px;">Nessun SAL ancora registrato — documento provvisorio</div>`}
      </div>

      <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid ${coloreDoc};padding:10px 0 12px;margin-bottom:10px;">
        <div>${logoHtml}<div style="font-size:10px;color:#6b7280;margin-top:4px;">${orgInfo?.indirizzo||""}${orgInfo?.piva?` · P.IVA ${orgInfo.piva}`:""}</div></div>
        <div style="text-align:right;">
          <div style="font-size:9px;color:#7c3aed;font-weight:700;">${codiceCommessa?`[${codiceCommessa}] `:""}${clienteNome||""}</div>
          <div style="font-size:16px;font-weight:900;color:${coloreDoc};margin-top:2px;">${computo.titolo||projectName||"Computo Metrico / SAL"}</div>
        </div>
      </div>

      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:10px 14px;margin-bottom:14px;font-size:10px;">
        <div><div style="color:#9ca3af;text-transform:uppercase;font-size:8px;margin-bottom:2px;">Cliente</div><strong>${clienteNome||"—"}</strong></div>
        <div><div style="color:#9ca3af;text-transform:uppercase;font-size:8px;margin-bottom:2px;">Commessa</div><strong>${codiceCommessa||"—"} ${projectName?"— "+projectName:""}</strong></div>
        <div><div style="color:#9ca3af;text-transform:uppercase;font-size:8px;margin-bottom:2px;">Responsabile</div><strong>${responsabileNome||"—"}</strong></div>
        <div><div style="color:#9ca3af;text-transform:uppercase;font-size:8px;margin-bottom:2px;">Data emissione</div><strong>${ultimoPeriodoStampa?dataItaliana(ultimoPeriodoStampa.data):"—"}</strong></div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Pos</th><th>Descrizione</th><th>U.M.</th><th style="text-align:right;">Q.tà</th><th style="text-align:right;">Imp.Unit.</th><th style="text-align:right;">Totale</th>
            ${intestazioniSal}
          </tr>
          <tr>
            <th colspan="6"></th>
            ${sottointestazioniSal}
          </tr>
        </thead>
        <tbody>${righeHtml}</tbody>
      </table>

      <div style="display:flex;gap:20px;margin-top:18px;flex-wrap:wrap;">
        ${periodi.length>0 ? `<div style="flex:1;min-width:260px;">
          <div style="font-size:10px;font-weight:700;color:${coloreDoc};text-transform:uppercase;margin-bottom:6px;">📊 Storico SAL</div>
          <table style="margin-top:0;">
            <thead><tr><th>ID SAL</th><th>Data</th><th style="text-align:right;">Avanz. complessivo</th></tr></thead>
            <tbody>${storicoHtml}</tbody>
          </table>
        </div>` : ""}
        <div style="max-width:480px;flex:1;min-width:280px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px 16px;font-size:11px;height:fit-content;">
          <div style="display:flex;justify-content:space-between;padding:2px 0;"><span>Importo complessivo commessa</span><strong>€${fmt(dati.totaleGenerale)}</strong></div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;"><span>Avanzamento precedente</span><strong>${fmtPerc(dati.avanzamentoPrecedente)}%</strong></div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;"><span>Avanzamento del periodo</span><strong>${fmtPerc(dati.avanzamentoDelPeriodo)}%</strong></div>
          <div style="display:flex;justify-content:space-between;padding:4px 0;border-top:1px solid #e5e7eb;margin-top:4px;font-weight:700;color:#7c3aed;"><span>Avanzamento complessivo</span><span>${fmtPerc(dati.avanzamentoComplessivo)}%</span></div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;"><span>Importo SAL maturato (progressivo)</span><strong>€${fmt(dati.salMaturato)}</strong></div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;"><span>Fatturato</span><strong>€${fmt(dati.fatturato)}</strong></div>
          <div style="display:flex;justify-content:space-between;padding:4px 0;border-top:1px solid #e5e7eb;margin-top:4px;font-weight:700;"><span>Da fatturare</span><span>€${fmt(dati.daFatturare)}</span></div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-weight:700;"><span>Residuo contrattuale</span><span>€${fmt(dati.residuoContrattuale)}</span></div>
        </div>
      </div>

      ${periodi.length>0 ? `<div style="margin-top:16px;font-size:10.5px;line-height:1.6;">
        <div>Il presente Stato di Avanzamento Lavori rappresenta le lavorazioni eseguite e contabilizzate alla data del <strong>${dataItaliana(periodi[periodi.length-1].data)}</strong>.</div>
        <div>Le lavorazioni non riportate nel presente Stato Avanzamento Lavori saranno oggetto di contabilizzazione nei successivi SAL, ove applicabile.</div>
      </div>` : ""}

      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;margin-top:26px;font-size:10.5px;page-break-inside:avoid;">
        <div><strong>Redatto da</strong><div style="margin-top:16px;">${computo.redattoDa||""}</div></div>
        <div><strong>Data</strong><div style="margin-top:16px;">${dataItaliana(computo.dataChiusura) || new Date().toLocaleDateString("it-IT")}</div></div>
        <div><strong>Per accettazione</strong><div style="margin-top:30px;border-bottom:1px solid #111;width:200px;"></div></div>
      </div>
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print";
      btn.style.cssText = "position:fixed;top:16px;right:16px;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(124,58,237,.3);z-index:9999;";
      btn.onclick = () => win.print();
      win.document.body.appendChild(btn);
    }, 50);
  };

  const salvaPeriodo = async () => {
    if (!await confirm(`Salvare definitivamente il SAL al ${dataItaliana(periodoAperto.data)}? Da questo momento le percentuali di questo periodo non saranno più modificabili.`,{type:"danger"})) return;
    const nuoviPeriodi = periodi.map(p => p.id===periodoAperto.id ? {...p, stato:"salvato"} : p);
    try {
      await updateDoc(doc(db,"computiSAL",computo.id), { periodi: nuoviPeriodi, updatedAt: serverTimestamp() });
      addToast("SAL salvato — non più modificabile ✅","success");
    } catch (err) {
      console.error("Errore salvataggio periodo SAL:", err);
      addToast("Errore nel salvare il SAL: "+(err?.message||"errore sconosciuto"),"error");
    }
  };

  // Si può riaprire SOLO l'ultimo SAL salvato, e solo se non ne è già stato creato uno
  // successivo — esattamente le stesse condizioni per cui il fatturato resta bloccato.
  const ultimoSalvato = periodi.length>0 && !periodoAperto ? periodi[periodi.length-1] : null;
  const riapriPeriodo = async () => {
    if (!await confirm(`Riaprire il SAL al ${dataItaliana(ultimoSalvato.data)}? Tornerà modificabile, e non potrai crearne uno nuovo finché non lo richiudi.`,{type:"danger"})) return;
    const nuoviPeriodi = periodi.map(p => p.id===ultimoSalvato.id ? {...p, stato:"bozza"} : p);
    try {
      await updateDoc(doc(db,"computiSAL",computo.id), { periodi: nuoviPeriodi, updatedAt: serverTimestamp() });
      addToast("SAL riaperto ✅","success");
    } catch (err) {
      console.error("Errore riapertura periodo SAL:", err);
      addToast("Errore nel riaprire il SAL: "+(err?.message||"errore sconosciuto"),"error");
    }
  };

  // Il fatturato resta bloccato mentre tutti i SAL sono salvati — riprende a essere
  // modificabile solo quando si apre un nuovo periodo (o si riapre l'ultimo).
  const fatturatoBloccato = periodi.length>0 && !periodoAperto;

  const aggiornaFatturato = (valore) => {
    if (fatturatoDaRichieste) return;
    updateDoc(doc(db,"computiSAL",computo.id), { fatturato: parseFloat(valore)||0, updatedAt: serverTimestamp() });
  };
  const aggiornaTitolo = (valore) => {
    updateDoc(doc(db,"computiSAL",computo.id), { titolo: valore, updatedAt: serverTimestamp() });
  };
  const aggiornaRedattoDa = (valore) => {
    updateDoc(doc(db,"computiSAL",computo.id), { redattoDa: valore, updatedAt: serverTimestamp() });
  };
  // La data di un periodo resta sempre correggibile, anche dopo il salvataggio — è un dato
  // amministrativo (può servire correggere un errore di battitura), diverso dalle
  // percentuali che restano bloccate per davvero una volta salvate.
  const aggiornaDataPeriodo = (periodoId, nuovaData) => {
    updateDoc(doc(db,"computiSAL",computo.id), {
      periodi: periodi.map(p => p.id===periodoId ? {...p, data:nuovaData} : p),
      updatedAt: serverTimestamp(),
    });
  };
  const aggiornaDataChiusura = (valore) => {
    updateDoc(doc(db,"computiSAL",computo.id), { dataChiusura: valore, updatedAt: serverTimestamp() });
  };
  const aggiornaColoreRiga = (id, colore) => {
    salvaRighe(righe.map(r => r.id===id ? {...r, colore} : r));
  };
  const [tavolozzaAperta, setTavolozzaAperta] = useState(null);
  const [ricercaListinoAperta, setRicercaListinoAperta] = useState(null); // id riga con popup aperto
  const [testoRicercaListino, setTestoRicercaListino] = useState("");
  const prelevaDaListino = (rigaId, v) => {
    aggiornaRiga(rigaId, "descrizione", v.descrizione);
    if (v.unitaMisura) aggiornaRiga(rigaId, "um", v.unitaMisura);
    if (v.prezzoUnitario) aggiornaRiga(rigaId, "prezzoUnitario", v.prezzoUnitario);
    setRicercaListinoAperta(null); setTestoRicercaListino("");
  };

  const inp = {width:"100%",padding:"4px 6px",border:"1px solid #e5e7eb",borderRadius:5,fontSize:11.5,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box",color:"#111827"};
  const num = {...inp, textAlign:"right"};
  const cell = {padding:"3px 5px",borderBottom:"1px solid #f3f4f6",verticalAlign:"top"};

  if (!caricato) return <div style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Caricamento...</div>;

  if (!computo) {
    return (
      <div style={{padding:40,textAlign:"center"}}>
        <div style={{fontSize:48,marginBottom:12,opacity:.15}}>📐</div>
        <div style={{fontSize:14,fontWeight:700,color:"#374151",marginBottom:6}}>Nessun Computo Metrico / SAL per questa commessa</div>
        <div style={{fontSize:12,color:"#9ca3af",marginBottom:16}}>Crealo una volta sola — crescerà nel tempo con ogni nuovo Stato Avanzamento Lavori</div>
        <button onClick={creaComputo} className="btn btn-primary" style={{padding:"7px 18px",fontSize:12}}>
          <Plus size={13}/> Crea Computo Metrico
        </button>
      </div>
    );
  }

  return (
    <div style={{padding:20,height:"100%",overflowY:"auto"}}>
      <div style={{marginBottom:14}}>
        <div style={{fontSize:11.5,fontWeight:700,color:"#7c3aed"}}>{codiceCommessa?`[${codiceCommessa}] `:""}{clienteNome||"—"}</div>
        <input value={computo.titolo||""} onChange={e=>aggiornaTitolo(e.target.value)}
          placeholder="Es. Impianto elettrico nuova sede — descrizione generale della commessa"
          style={{width:"100%",marginTop:3,padding:"5px 8px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:14,fontWeight:700,fontFamily:"'Inter',sans-serif",outline:"none",color:"#111827",boxSizing:"border-box"}}/>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6,flexWrap:"wrap"}}>
        <h3 style={{fontSize:15,fontWeight:800,color:"#1e1b4b",margin:0}}>📐 Computo Metrico / SAL</h3>
        <button onClick={esportaExcel} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",border:"1px solid #059669",borderRadius:7,background:"#f0fdf4",color:"#059669",fontSize:12,fontWeight:700,cursor:"pointer"}}>
          <FileSpreadsheet size={13}/> Excel
        </button>
        <button onClick={()=>!importazioneBloccata && setShowImporta(true)} disabled={importazioneBloccata}
          title={importazioneBloccata ? "Non disponibile: è già stato salvato almeno un SAL su questa commessa. L'importazione serve solo per la fase iniziale, prima di registrare avanzamenti." : ""}
          style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",border:`1px solid ${importazioneBloccata?"#d1d5db":"#0369a1"}`,borderRadius:7,background:importazioneBloccata?"#f9fafb":"#eff6ff",color:importazioneBloccata?"#9ca3af":"#0369a1",fontSize:12,fontWeight:700,cursor:importazioneBloccata?"not-allowed":"pointer"}}>
          <Upload size={13}/> Importa da Excel
        </button>
        {importazioneBloccata && (
          <span style={{fontSize:10.5,color:"#9ca3af",fontStyle:"italic"}}>
            🔒 non disponibile — già salvato almeno un SAL su questa commessa
          </span>
        )}
        <button onClick={stampaPDF} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",border:"1px solid #7c3aed",borderRadius:7,background:"#faf5ff",color:"#7c3aed",fontSize:12,fontWeight:700,cursor:"pointer"}}>
          🖨️ PDF
        </button>
        {periodoAperto ? (
          <button onClick={salvaPeriodo} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",borderRadius:7,border:"none",background:"#059669",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            💾 Salva SAL al {dataItaliana(periodoAperto.data)}
          </button>
        ) : (
          <>
            <button onClick={()=>setShowNuovoPeriodo(true)} className="btn btn-primary" style={{padding:"5px 12px",fontSize:12}}>
              <Calendar size={13}/> + Nuovo SAL
            </button>
            {ultimoSalvato && (
              <button onClick={riapriPeriodo} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",borderRadius:7,border:"1px solid #d97706",background:"#fffbeb",color:"#d97706",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                🔓 Riapri SAL al {dataItaliana(ultimoSalvato.data)}
              </button>
            )}
          </>
        )}
      </div>
      <p style={{fontSize:11.5,color:"#6b7280",marginBottom:12}}>{periodi.length} periodo/i SAL registrato/i · le righe si aggiungono da sole, scrivi e basta</p>

      {periodoAperto && (
        <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"7px 12px",marginBottom:12,fontSize:11,color:"#5b21b6"}}>
          💜 Il SAL al {dataItaliana(periodoAperto.data)} è ancora una bozza — puoi modificare le percentuali finché vuoi. Una volta premuto "Salva SAL", quel periodo si blocca per sempre e solo allora potrai crearne uno nuovo.
        </div>
      )}

      {showNuovoPeriodo && (
        <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:"12px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
          <label style={{fontSize:12,fontWeight:600,color:"#5b21b6"}}>Data del nuovo SAL:</label>
          <input type="date" value={dataNuovoPeriodo} onChange={e=>setDataNuovoPeriodo(e.target.value)}
            style={{padding:"5px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12}}/>
          <button onClick={aggiungiPeriodo} style={{padding:"5px 12px",borderRadius:6,border:"none",background:"#7c3aed",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}}>Crea</button>
          <button onClick={()=>setShowNuovoPeriodo(false)} style={{padding:"5px 12px",borderRadius:6,border:"1px solid #e5e7eb",background:"#fff",fontSize:12,cursor:"pointer"}}>Annulla</button>
        </div>
      )}

      <div style={{overflowX:"auto",border:"1px solid #e5e7eb",borderRadius:10,marginBottom:16}}>
        <table style={{borderCollapse:"collapse",fontSize:11.5,minWidth:990}}>
          <thead>
            <tr style={{background:"#1e1b4b"}}>
              <th style={{padding:"6px 5px",color:"#fff",fontSize:9,fontWeight:700,textTransform:"uppercase",width:34}}>Pos</th>
              <th style={{padding:"6px 5px",color:"#fff",fontSize:9,fontWeight:700,textTransform:"uppercase",minWidth:340}}>Descrizione</th>
              <th style={{padding:"6px 5px",color:"#fff",fontSize:9,fontWeight:700,textTransform:"uppercase",width:50}}>U.M.</th>
              <th style={{padding:"6px 5px",color:"#fff",fontSize:9,fontWeight:700,textTransform:"uppercase",width:60}}>Q.tà</th>
              <th style={{padding:"6px 5px",color:"#fff",fontSize:9,fontWeight:700,textTransform:"uppercase",width:85}}>Imp. unit.</th>
              <th style={{padding:"6px 5px",color:"#fff",fontSize:9,fontWeight:700,textTransform:"uppercase",width:95}}>Totale</th>
              {periodi.map(per=>(
                <th key={per.id} colSpan={3} style={{padding:"6px 5px",color:"#fff",fontSize:9,fontWeight:700,textAlign:"center",borderLeft:"2px solid rgba(255,255,255,.2)"}}>
                  <div style={{marginBottom:4,textTransform:"uppercase"}}>SAL al</div>
                  <input type="date" value={per.data||""} onChange={e=>aggiornaDataPeriodo(per.id,e.target.value)}
                    style={{fontSize:11,fontWeight:700,padding:"4px 6px",borderRadius:5,border:"2px solid #fbbf24",background:"#fff",color:"#111827",width:"100%",boxSizing:"border-box",cursor:"pointer"}}/>
                </th>
              ))}
              <th style={{width:30}}></th>
            </tr>
            {periodi.length>0 && (
              <tr style={{background:"#f9fafb"}}>
                <th colSpan={6}></th>
                {periodi.map(per=>(
                  <React.Fragment key={per.id}>
                    <th style={{padding:"4px",fontSize:8.5,color:"#374151",fontWeight:700,width:44,borderLeft:"2px solid #e5e7eb"}}>%</th>
                    <th style={{padding:"4px",fontSize:8.5,color:"#374151",fontWeight:700,width:80}}>Totale SAL</th>
                    <th style={{padding:"4px",fontSize:8.5,color:"#374151",fontWeight:700,width:90}}>Note</th>
                  </React.Fragment>
                ))}
                <th></th>
              </tr>
            )}
          </thead>
          <tbody>
            {righe.map((r, rIdx) => {
              const tot = totaleRiga(r);
              const pos = dati.numerazione[r.id];
              return (
                <tr key={r.id} style={{background: r.colore || (r.titolo ? "#f5f3ff" : "#fff")}}>
                  <td style={{...cell,textAlign:"center",fontWeight:700,color:"#d97706"}}>{pos||""}</td>
                  <td style={{...cell,position:"relative"}}>
                    <div style={{display:"flex",alignItems:"flex-start",gap:4}}>
                      <button type="button" onClick={()=>aggiornaRiga(r.id,"titolo",!r.titolo)}
                        title="Riga titolo/categoria (tutta in grassetto)"
                        style={{flexShrink:0,marginTop:2,width:16,height:16,borderRadius:3,border:`1px solid ${r.titolo?"#7c3aed":"#d1d5db"}`,background:r.titolo?"#7c3aed":"#fff",color:"#fff",fontSize:10,fontWeight:900,cursor:"pointer",lineHeight:1}}>
                        {r.titolo?"B":""}
                      </button>
                      <button type="button" onClick={()=>setTavolozzaAperta(tavolozzaAperta===r.id?null:r.id)}
                        title="Colore riga"
                        style={{flexShrink:0,marginTop:2,width:16,height:16,borderRadius:3,border:"1px solid #d1d5db",background:r.colore||"#fff",cursor:"pointer"}}/>
                      {tavolozzaAperta===r.id && (
                        <TavolozzaColori colore={r.colore} onScegli={c=>aggiornaColoreRiga(r.id,c)} onChiudi={()=>setTavolozzaAperta(null)}/>
                      )}
                      <input value={r.descrizione||""} onChange={e=>aggiornaRiga(r.id,"descrizione",e.target.value)}
                        onKeyDown={e=>onKeyDownNav(e)} data-r={rIdx} data-c={0}
                        placeholder="Descrizione lavorazione — **testo** per il grassetto"
                        style={{...inp, fontWeight: r.titolo?800:400}}/>
                      {listinoMateriali.length>0 && (
                        <button type="button" onClick={()=>{setRicercaListinoAperta(ricercaListinoAperta===r.id?null:r.id); setTestoRicercaListino("");}}
                          title="Cerca dal Listino Materiali"
                          style={{flexShrink:0,padding:"0 6px",border:"1px solid #7c3aed",borderRadius:5,background:"#faf5ff",color:"#7c3aed",cursor:"pointer",fontSize:11}}>🔍</button>
                      )}
                      {ricercaListinoAperta===r.id && (
                        <div style={{position:"absolute",zIndex:30,top:"100%",left:0,right:0,background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,.12)",marginTop:2,maxHeight:240,overflowY:"auto"}}>
                          <input autoFocus value={testoRicercaListino} onChange={e=>setTestoRicercaListino(e.target.value)} placeholder="Cerca per descrizione o codice..."
                            style={{width:"100%",padding:"7px 10px",border:"none",borderBottom:"1px solid #f3f4f6",fontSize:12,outline:"none",boxSizing:"border-box"}}/>
                          {testoRicercaListino.trim().length<2 ? (
                            <div style={{padding:10,fontSize:11,color:"#9ca3af"}}>Scrivi almeno 2 lettere per cercare</div>
                          ) : listinoMateriali.filter(v=>{
                              const t = testoRicercaListino.toLowerCase();
                              return (v.descrizione||"").toLowerCase().includes(t) || (v.codiceArticolo||"").toLowerCase().includes(t);
                            }).slice(0,20).map(v=>(
                            <div key={v.id} onClick={()=>prelevaDaListino(r.id,v)}
                              style={{padding:"7px 10px",cursor:"pointer",borderBottom:"1px solid #f9fafb",fontSize:12}}
                              onMouseEnter={e=>e.currentTarget.style.background="#faf5ff"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                              <div style={{fontWeight:600,color:"#111827"}}>{v.descrizione}</div>
                              <div style={{fontSize:10.5,color:"#9ca3af"}}>€{fmt(v.prezzoUnitario)} {v.fornitore?`· ${v.fornitore}`:""}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={cell}><input value={r.um||""} onChange={e=>aggiornaRiga(r.id,"um",e.target.value)} onKeyDown={e=>onKeyDownNav(e)} data-r={rIdx} data-c={1} style={inp} placeholder="mt/n/corpo"/></td>
                  <td style={cell}><input type="number" min="0" value={r.quantita||""} onChange={e=>aggiornaRiga(r.id,"quantita",Math.max(0,parseFloat(e.target.value)||0)||"")} onKeyDown={e=>onKeyDownNav(e)} data-r={rIdx} data-c={2} style={num}/></td>
                  <td style={cell}><input type="number" min="0" step="0.01" value={r.prezzoUnitario||""} onChange={e=>aggiornaRiga(r.id,"prezzoUnitario",Math.max(0,parseFloat(e.target.value)||0)||"")} onKeyDown={e=>onKeyDownNav(e)} data-r={rIdx} data-c={3} style={num}/></td>
                  <td style={{...cell,textAlign:"right",fontWeight:700,color:tot>0?"#059669":"#d1d5db"}}>{tot>0?`€${fmt(tot)}`:"—"}</td>
                  {periodi.map((per,pIdx)=>{
                    const bloccato = per.stato==="salvato";
                    const cPerc = 4+pIdx*2, cNote = 5+pIdx*2;
                    return (
                      <React.Fragment key={per.id}>
                        <td style={{...cell,borderLeft:"2px solid #f3f4f6",background:bloccato?"#fafafa":"transparent"}}>
                          <input type="number" min="0" max="100" disabled={bloccato} value={r.sal?.[per.id]?.perc||""} onChange={e=>aggiornaSalRiga(r.id,per.id,"perc",Math.min(100,Math.max(0,parseFloat(e.target.value)||0))||"")} onKeyDown={e=>onKeyDownNav(e)} data-r={rIdx} data-c={cPerc} style={{...num,width:52,padding:"5px 4px"}}/>
                        </td>
                        <td style={{...cell,textAlign:"right",color:"#6b7280",background:bloccato?"#fafafa":"transparent"}}>{tot>0 && r.sal?.[per.id]?.perc?`€${fmt(totaleSalRigaPeriodo(r,per.id))}`:"—"}</td>
                        <td style={{...cell,background:bloccato?"#fafafa":"transparent"}}><input disabled={bloccato} value={r.sal?.[per.id]?.note||""} onChange={e=>aggiornaSalRiga(r.id,per.id,"note",e.target.value)} onKeyDown={e=>onKeyDownNav(e)} data-r={rIdx} data-c={cNote} style={{...inp,width:85}}/></td>
                      </React.Fragment>
                    );
                  })}
                  <td style={cell}>
                    <button onClick={()=>eliminaRiga(r.id)} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5"}}><Trash2 size={12}/></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {dati.righeSforate.length>0 && (
        <div style={{background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,padding:"8px 12px",marginBottom:16,fontSize:11.5,color:"#991b1b"}}>
          ⚠️ Attenzione: {dati.righeSforate.length} riga/e superano il 100% sommando tutti i SAL — controllale.
        </div>
      )}

      <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20,maxWidth:600}}>
        <h4 style={{fontSize:13,fontWeight:800,color:"#1e1b4b",marginBottom:14}}>📊 Riepilogo</h4>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px 24px",fontSize:12.5,marginBottom:14}}>
          <div>Importo complessivo commessa</div><div style={{textAlign:"right",fontWeight:700}}>€{fmt(dati.totaleGenerale)}</div>
          <div>Avanzamento precedente</div><div style={{textAlign:"right",fontWeight:700}}>{fmtPerc(dati.avanzamentoPrecedente)}%</div>
          <div>Avanzamento del periodo</div><div style={{textAlign:"right",fontWeight:700}}>{fmtPerc(dati.avanzamentoDelPeriodo)}%</div>
          <div style={{borderTop:"1px solid #e5e7eb",paddingTop:6,fontWeight:700}}>Avanzamento complessivo</div><div style={{borderTop:"1px solid #e5e7eb",paddingTop:6,textAlign:"right",fontWeight:800,color:"#7c3aed"}}>{fmtPerc(dati.avanzamentoComplessivo)}%</div>
          <div>Importo SAL maturato (progressivo)</div><div style={{textAlign:"right",fontWeight:700}}>€{fmt(dati.salMaturato)}</div>
        </div>

        {previsione && !previsione.completato && !previsione.fermo && (
          <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:12}}>
            <div style={{fontWeight:700,color:"#5b21b6"}}>📅 Completamento previsto: {previsione.dataStimata.toLocaleDateString("it-IT")}</div>
            <div style={{fontSize:10.5,color:"#7c3aed",marginTop:2}}>Al ritmo medio tenuto negli ultimi {previsione.nSalDiOsservazione} SAL — è una previsione statistica, non una scadenza, si aggiorna da sola a ogni nuovo SAL salvato.</div>
          </div>
        )}
        {previsione?.fermo && (
          <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:11.5,color:"#92400e"}}>
            📅 Nessun avanzamento misurabile tra i SAL salvati — previsione di completamento non disponibile al momento.
          </div>
        )}
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
          <label style={{fontSize:12,fontWeight:600,color:"#374151"}}>Fatturato:</label>
          {fatturatoDaRichieste ? (<>
            <strong style={{fontSize:13}}>€{fmt(fatturato)}</strong>
            <span style={{fontSize:10.5,color:"#6b7280"}}>— dalle richieste di fatturazione segnate «Fatturata» dall'amministrazione: si aggiorna da solo</span>
          </>) : (<>
            <input type="number" min="0" step="0.01" disabled={fatturatoBloccato} value={fatturato||""} onChange={e=>aggiornaFatturato(e.target.value)}
              style={{...num,width:140,background:fatturatoBloccato?"#f9fafb":"#fff"}}/>
            {fatturatoBloccato ? (
              <span style={{fontSize:10.5,color:"#d97706",fontWeight:600}}>🔒 bloccato — riprende a essere modificabile aprendo un nuovo SAL o riaprendo l'ultimo</span>
            ) : (
              <span style={{fontSize:10.5,color:"#9ca3af"}}>— da scrivere a mano finché la commessa non ha richieste di fatturazione; con la prima richiesta si calcola da solo</span>
            )}
          </>)}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px 24px",fontSize:12.5}}>
          <div>Da fatturare (maturato − fatturato)</div><div style={{textAlign:"right",fontWeight:700}}>€{fmt(dati.daFatturare)}</div>
          {richiestoInAttesa > 0 && <>
            <div style={{color:"#6b7280"}}>Già chiesto in fattura, in attesa dell'amministrazione</div><div style={{textAlign:"right",color:"#6b7280"}}>€{fmt(dati.richiestoInAttesa)}</div>
            <div>Da chiedere in fattura</div><div style={{textAlign:"right",fontWeight:700,color:"#7c3aed"}}>€{fmt(dati.nonRichiesto)}</div>
          </>}
          <div>Residuo contrattuale</div><div style={{textAlign:"right",fontWeight:700}}>€{fmt(dati.residuoContrattuale)}</div>
        </div>

        {periodi.length>0 && (
          <div style={{marginTop:20,paddingTop:16,borderTop:"1px solid #e5e7eb",fontSize:11.5,color:"#374151",lineHeight:1.6}}>
            <div>Il presente Stato di Avanzamento Lavori rappresenta le lavorazioni eseguite e contabilizzate alla data del <strong>{dataItaliana(periodi[periodi.length-1].data)}</strong>.</div>
            <div>Le lavorazioni non riportate nel presente Stato Avanzamento Lavori saranno oggetto di contabilizzazione nei successivi SAL, ove applicabile.</div>
          </div>
        )}

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginTop:20}}>
          <div>
            <label style={{fontSize:10.5,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Redatto da</label>
            <input value={computo.redattoDa||""} onChange={e=>aggiornaRedattoDa(e.target.value)} placeholder="Es. Rossi Impianti Srl — Per.Ind. Mario Rossi"
              style={{width:"100%",padding:"5px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",color:"#111827",boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{fontSize:10.5,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Data</label>
            <input type="date" value={computo.dataChiusura || toLocalDateStr(new Date())} onChange={e=>aggiornaDataChiusura(e.target.value)}
              style={{width:"100%",padding:"5px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",color:"#111827",boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{fontSize:10.5,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Per accettazione</label>
            <div style={{borderBottom:"1px solid #9ca3af",height:22}}></div>
          </div>
        </div>
      </div>

      {showImporta && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>{setShowImporta(false);setPassoImporta(1);}}>
          <div style={{background:"#fff",borderRadius:14,padding:24,width:600,maxHeight:"80vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <h3 style={{fontSize:15,fontWeight:800,color:"#1e1b4b",margin:0}}>📥 Importa da Excel</h3>
              <button onClick={()=>{setShowImporta(false);setPassoImporta(1);}} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={18}/></button>
            </div>

            {passoImporta===1 && (
              <div>
                <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:11.5,color:"#5b21b6"}}>
                  💜 Funziona bene se il tuo file ha una riga per ogni voce, con descrizione/quantità/prezzo in colonne (anche in ordine diverso dal solito, sei tu a dirci quale colonna è cosa al passo successivo). Fogli con celle unite o strutture molto particolari potrebbero non importarsi correttamente — in quel caso conviene ricopiare i dati a mano la prima volta.
                </div>
                <label style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8,padding:"30px 20px",border:"2px dashed #d1d5db",borderRadius:10,cursor:"pointer",background:"#fafafa"}}>
                  <Upload size={28} color="#9ca3af"/>
                  <span style={{fontSize:13,fontWeight:600,color:"#374151"}}>Clicca per scegliere un file Excel (.xlsx)</span>
                  <input type="file" accept=".xlsx,.xls" style={{display:"none"}} onChange={e=>{ if(e.target.files[0]) leggiFileImportazione(e.target.files[0]); }}/>
                </label>
              </div>
            )}

            {passoImporta===2 && (
              <div>
                <p style={{fontSize:12,color:"#6b7280",marginBottom:14}}>Trovate {righeGrezze[0]?.length||0} colonne, {righeGrezze.length-1} righe di dati. Dicci a cosa corrisponde ogni colonna del tuo file:</p>
                <div style={{maxHeight:280,overflowY:"auto",marginBottom:16}}>
                  {righeGrezze[0]?.map((intestazione,i) => (
                    <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:"1px solid #f3f4f6"}}>
                      <div style={{flex:1,fontSize:12,fontWeight:600,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {String(intestazione||`Colonna ${i+1}`)}
                        <div style={{fontSize:10,color:"#9ca3af",fontWeight:400}}>{String(righeGrezze[1]?.[i]||"")}</div>
                      </div>
                      <select value={mappaColonne[i]||""} onChange={e=>setMappaColonne(m=>({...m,[i]:e.target.value}))}
                        style={{padding:"5px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,minWidth:220}}>
                        {CAMPI_MAPPABILI.map(c=><option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setPassoImporta(1)} style={{padding:"7px 14px",border:"1px solid #e5e7eb",borderRadius:7,background:"#fff",fontSize:12,cursor:"pointer"}}>← Indietro</button>
                  <button onClick={()=>setPassoImporta(3)} className="btn btn-primary" style={{padding:"7px 14px",fontSize:12}}>Continua →</button>
                </div>
              </div>
            )}

            {passoImporta===3 && (
              <div>
                <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:11.5,color:"#5b21b6"}}>
                  💜 Arrivano i valori (descrizione, quantità, prezzo), non le formule del tuo Excel — i calcoli li rifà Nexiora con la sua logica, come per le righe scritte a mano. Le righe che importi si aggiungono a quelle che hai già, senza toccarle.
                </div>
                <p style={{fontSize:12,color:"#374151",marginBottom:10,fontWeight:600}}>Anteprima ({righeGrezze.length-1} righe da importare):</p>
                <div style={{maxHeight:220,overflowY:"auto",border:"1px solid #e5e7eb",borderRadius:8,marginBottom:16}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <tbody>
                      {righeGrezze.slice(1,9).map((r,i)=>{
                        const idxD = Object.entries(mappaColonne).find(([,v])=>v==="descrizione")?.[0];
                        return <tr key={i} style={{borderBottom:"1px solid #f3f4f6"}}><td style={{padding:"5px 8px"}}>{String(r[idxD]||"—")}</td></tr>;
                      })}
                      {righeGrezze.length-1>8 && <tr><td style={{padding:"5px 8px",color:"#9ca3af"}}>… e altre {righeGrezze.length-1-8} righe</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setPassoImporta(2)} style={{padding:"7px 14px",border:"1px solid #e5e7eb",borderRadius:7,background:"#fff",fontSize:12,cursor:"pointer"}}>← Indietro</button>
                  <button onClick={confermaImportazione} disabled={importando} className="btn btn-primary" style={{padding:"7px 14px",fontSize:12}}>
                    {importando?"Importazione...":"✅ Conferma importazione"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
